import { AllCamerasIdGroup } from '@/globals/constants';
import { abortRequest } from '@/services/api/http.service';
import { authService } from '@/services/auth.service';
import { getCameraGroupList } from '@/services/ccs.group.service';
import { videoService } from '@/services/video.service';
import CameraService from '@/services/CameraService';
import { getDayjsFromSecomDate } from '@/utils/date';
import { convertHoursToDays, sortObjectsByIds } from '@/utils/helpers';
import cloneDeep from 'clone-deep';
import dayjs from 'dayjs';
import Vue from 'vue';
import router from '@/router';
import { eventService } from '@/services/event.service';

const state = {
  appReady: false,
  webVersion: null,
  networkOnline: true,
  userInfo: {},
  cameraList: [],
  homeCameraGroups: {},
  homeCameraGroupList: [],
  currentHomeGroup: null,
  isLoading: false,
  cameraListLoading: false,
  initialPage: null,
  cameras: {
    0: {},
    1: {},
    2: {},
  },
  currentCameraPolicy: { access: true, liveOnly: true },
  cameraById: {},
  iam: {},
  area: false,
  areaCoordinates: [],
  testMode: false,
  searchKeys: [
    {
      key: '1',
      value: '動体検知',
      order: 1,
      hide: false,
    },
    {
      key: '2',
      value: 'エリア立入検知',
      order: 3,
      hide: true,
    },
    {
      key: '3',
      value: '滞留検知',
      order: 4,
      hide: true,
    },
    {
      key: '4',
      value: '顔検知',
      order: 6,
      hide: true,
    },
    {
      key: '5',
      value: 'ライン通過検知',
      order: 2,
      hide: true,
    },
    {
      key: '6',
      value: '置き去り検知',
      order: 7,
      hide: true,
    },
    {
      key: '7',
      value: '持ち去り検知',
      order: 8,
      hide: true,
    },
    {
      key: '8',
      value: '混雑検知',
      order: 5,
      hide: true,
    },
    {
      key: '9',
      value: '駆け出し検知',
      order: 9,
      hide: true,
    },
    {
      key: '10',
      value: '不審な動き',
      order: 10,
      hide: true,
    },
  ],
  noCameraFound: false,
};

// Getter functions
const getters = {
  shouldFetchCameraList(state) {
    return !state.cameraList?.length && !state.cameraListLoading;
  },
  currentEnv() {
    return process.env.VUE_APP_MODE;
  },
  isDEV(state, getters) {
    return getters.currentEnv === 'development';
  },
  isSTG(state, getters) {
    return getters.currentEnv === 'staging';
  },
  isPRD(state, getters) {
    return getters.currentEnv === 'production';
  },
  getArea() {
    return JSON.parse(JSON.stringify(state.area));
  },
  getAreaCoordinates() {
    return JSON.parse(JSON.stringify(state.areaCoordinates));
  },
  getCameraList() {
    return JSON.parse(JSON.stringify(state.cameraList));
  },
  getIam() {
    return JSON.parse(JSON.stringify(state.iam));
  },
  getSearchKeys(state) {
    return state.searchKeys
      .filter((key) => !key.hide)
      .sort((a, b) => a.order - b.order);
  },
  currentGroupCamList(state) {
    if (state.homeCameraGroups[state.currentHomeGroup] !== undefined) {
      return state.homeCameraGroups[state.currentHomeGroup].cameraList || [];
    } else {
      console.warn('No cameras found in current group');
      return [];
    }
  },
  maxRetentionPeriodHours(state, getters) {
    const currentGroupCameras = getters.currentGroupCamList;
    if (!currentGroupCameras || !currentGroupCameras.length) {
      return null;
    }
    return Math.max(
      ...currentGroupCameras.map((camera) => camera.retentionPeriod || 0)
    );
  },
  maxRetentionPeriodDays(state, getters) {
    const maxRetentionPeriodHours = getters.maxRetentionPeriodHours;
    if (!maxRetentionPeriodHours) {
      return null;
    }
    return convertHoursToDays(maxRetentionPeriodHours);
  },
  minRetentionDate(state, getters) {
    const maxRetentionPeriodDays = getters.maxRetentionPeriodDays;
    if (!maxRetentionPeriodDays) {
      return null;
    }
    return dayjs().subtract(maxRetentionPeriodDays, 'days').valueOf();
  },
  allCamList(state) {
    return state.homeCameraGroups?.[AllCamerasIdGroup]?.cameraList || [];
  },
  getUserInfo() {
    return { ...state.userInfo };
  },
  updateVersionExists(state) {
    return (
      state.cameraList.length &&
      state.cameraList.some(
        (camera) =>
          camera.UpdateVersion &&
          camera.UpdateVersion.some((u) => [0, 3].includes(u.updatingState))
      )
    );
  },
};

// Mutations
const mutations = {
  setArea(state, area) {
    state.area = area;
  },
  setAreaCoordinates(state, areaCoordinates) {
    state.areaCoordinates = areaCoordinates;
  },
  setCameraList(state, cameraList) {
    state.cameraList = cameraList;
  },
  setCameras(state, cameras) {
    state.cameras = cameras;
  },
  setCameraById(state, cameraById) {
    state.cameraById = cameraById;
  },
  setIam(state, iam) {
    state.iam = iam;
  },
  setSearchKeys(state, searchKeys) {
    state.searchKeys = searchKeys;
  },
  setUserInfo(state, userInfo) {
    state.userInfo = userInfo;
  },
  setAppReady(state, appReady) {
    state.appReady = appReady;
  },
  setWebVersion(state, webVersion) {
    state.webVersion = webVersion;
  },
  setNetworkOnline(state, networkOnline) {
    state.networkOnline = networkOnline;
  },
  setHomeCameraGroups(state, { groups, list }) {
    // replace the whole homeCameraGroups object
    Vue.set(state, 'homeCameraGroups', groups);

    // replace the whole homeCameraGroupList
    Vue.set(state, 'homeCameraGroupList', list);
  },
  updateCameraGroupOrder(state, { groupId, cameraList }) {
    // create independent empty arrays to avoid shared references
    Vue.set(
      state.homeCameraGroups[groupId],
      'cameraList',
      cloneDeep(cameraList)
    );
    Vue.set(
      state.homeCameraGroups[groupId],
      'cameraIds',
      cameraList.map((c) => c.id)
    );
    const groupIndex = state.homeCameraGroupList.findIndex(
      (group) => group.id === groupId
    );

    Vue.set(
      state.homeCameraGroupList[groupIndex],
      'cameraList',
      cloneDeep(cameraList)
    );
    Vue.set(
      state.homeCameraGroupList[groupIndex],
      'cameraIds',
      cameraList.map((c) => c.id)
    );
  },
  updateCamera(state, camera) {
    const camIndex = state.cameraList.findIndex((cam) => cam.id === camera.id);
    Object.keys(camera).forEach((camKey) => {
      const value = camera[camKey];
      Vue.set(state.cameraById[camera.id], camKey, value);
      Vue.set(state.cameraList[camIndex], camKey, value);
      Vue.set(state.cameras[camIndex], camKey, value);
    });
  },
  testMode(state, testMode) {
    state.testMode = testMode;
  },
  clearAll(state) {
    state.userInfo = {};
    state.cameraList = [];
    state.homeCameraGroups = {};
    state.homeCameraGroupList = [];
    state.currentHomeGroup = null;
    state.cameras = {
      0: {},
      1: {},
      2: {},
    };
    state.cameraById = {};
  },
  setIsLoading(state, value) {
    state.isLoading = value;
  },
  setCurrentHomeGroup(state, payload) {
    state.currentHomeGroup = payload;
  },
  setCurrentCameraPolicy(state, payload) {
    Vue.set(state.currentCameraPolicy, 'access', payload.access);
    Vue.set(state.currentCameraPolicy, 'liveOnly', payload.liveOnly);
  },
  setCameraListLoading(state, payload) {
    state.cameraListLoading = payload;
  },
  noCameraFound(state, payload) {
    state.noCameraFound = payload;
  },
  setInitialPage(state, page) {
    state.initialPage = page;
  },
  clearInitialPage(state) {
    state.initialPage = null;
  },
};

// Actions
const actions = {
  changeArea({ commit }, area) {
    commit('setArea', area);
  },
  changeAreaCoordinates({ commit }, areaCoordinates) {
    commit('setAreaCoordinates', areaCoordinates);
  },
  updateCamera({ commit }, camera) {
    commit('updateCamera', camera);
  },
  updateCameraLatencyMode({ state }, camera) {
    state.cameraById[camera.id].latencyMode = camera.latencyMode;
    const camIndex = state.cameraList.findIndex(
      (cam) => +cam.id === +camera.id
    );
    state.cameraList[camIndex].latencyMode = camera.latencyMode;
    state.cameras[camIndex].latencyMode = camera.latencyMode;
  },
  changeCameraList(
    { commit, dispatch, rootState },
    { cameraList, groups, noSelectedGroupChange = false } = {}
  ) {
    if (!cameraList) {
      console.warn('cameraList is undefined');
      return;
    }
    const cameras = {
      0: {},
      1: {},
      2: {},
    };
    let homeCameraGroups = {};
    const cameraById = {};
    const lowLatencyModeByIds = null;

    cameraList.forEach((camera, index) => {
      // map camera group with camera list when there is no `tagInfoList`

      camera.tagInfoList = groups.tags.filter((tag) =>
        tag.cameraIds.includes(camera.id)
      );
      // map home camera group
      if (camera.tagInfoList && camera.tagInfoList.length > 0) {
        const kind0Tags = camera.tagInfoList.filter((tag) => tag.kind === 0);
        kind0Tags.forEach((tag) => {
          if (!homeCameraGroups[tag.id]) {
            homeCameraGroups[tag.id] = { ...tag, cameraList: [] };
          }
          homeCameraGroups[tag.id].cameraList.push(camera);
        });
      }

      // map camera policy item list "CameraPolicy_LiveOnly"/"CameraPolicy_Access"
      if (camera.PolicyItemList) {
        const cameraPolicy = {
          access: true,
          liveOnly: true,
        };
        const accessPolicy = camera.PolicyItemList.find(
          (item) => item.name === 'CameraPolicy_Access'
        );
        const liveOnlyPolicy = camera.PolicyItemList.find(
          (item) => item.name === 'CameraPolicy_LiveOnly'
        );

        cameraPolicy.access = !!accessPolicy && accessPolicy.value === 1;
        cameraPolicy.liveOnly = !!liveOnlyPolicy && liveOnlyPolicy.value === 1;
        camera.cameraPolicy = cameraPolicy;
      }

      // map camera update version
      if (camera.UpdateVersion) {
        camera.UpdateVersion.forEach((version) => {
          version.createDateUnix = getDayjsFromSecomDate(
            version.createDate
          ).valueOf();
          // Sort versions in descending order
        });
        camera.UpdateVersion.sort((a, b) => {
          return b.createDateUnix - a.createDateUnix;
        });
      }
      if (camera.OperatingVersion) {
        camera.OperatingVersion.forEach((version) => {
          version.createDateUnix = getDayjsFromSecomDate(
            version.createDate
          ).valueOf();
          // Sort versions in descending order
        });
        camera.OperatingVersion.sort((a, b) => {
          return b.createDateUnix - a.createDateUnix;
        });
      }
      cameraList[index] = camera;
      camera.index = index;
      if (lowLatencyModeByIds) {
        const existedId = lowLatencyModeByIds.find(
          (item) => item.id === camera.id
        );
        if (existedId) {
          camera.latencyMode = existedId.latencyMode;
        } else {
          camera.latencyMode = 'HLS';
        }
      }
      cameras[index] = camera;
      cameraById[camera.id] = camera;
    });

    // check if there is a camera group with kind 0(all cameras group)
    // if not, add one
    const homeCameraGroupKeys = Object.keys(homeCameraGroups || {});

    if (
      rootState?.settings?.isCurrentAvatarOwner &&
      !homeCameraGroupKeys.some((key) => +key === AllCamerasIdGroup)
    ) {
      const allCamerasGroup = groups.tags.find(
        (t) => t.id === AllCamerasIdGroup
      );
      if (allCamerasGroup) {
        homeCameraGroups[AllCamerasIdGroup] = {
          ...allCamerasGroup,
          cameraList: [],
        };
      } else {
        console.warn(
          'Current avatar is not the owner of AllCamerasIdGroup',
          AllCamerasIdGroup
        );
      }
    }

    // sort camera list by cameraIds
    homeCameraGroupKeys.forEach((groupId) => {
      const group = homeCameraGroups[groupId];
      group.cameraList = sortObjectsByIds(group.cameraIds, group.cameraList);
    });

    // set mapped camera list to homeCameraGroups
    commit('setHomeCameraGroups', {
      groups: cloneDeep(homeCameraGroups),
      list: cloneDeep(Object.values(homeCameraGroups)),
    });
    commit('setCameraList', cameraList);
    commit('setCameras', cameras);
    commit('setCameraById', cameraById);
    dispatch(
      'settings/mapCameraGroup',
      { tags: groups.tags, noSelectedGroupChange },
      { root: true }
    );
  },
  changeIam({ commit }, iam) {
    commit('setIam', iam);
  },
  changeSearchKeys({ commit }, searchKeys) {
    commit('setSearchKeys', searchKeys);
  },
  async start({ commit }) {
    const userInfo = await authService.getCurrentUserInfo();
    commit('setUserInfo', userInfo);

    commit('setAppReady', true);
  },
  fetchCameraList() {
    return videoService.getCameraList();
  },
  setNetworkOnline({ commit }, networkOnline) {
    commit('setNetworkOnline', networkOnline);
  },
  setTestMode({ commit }, testMode) {
    commit('testMode', testMode);
  },
  clearAll({ commit }) {
    commit('clearAll');
  },
  abortHttpRequest(_, reqId) {
    abortRequest(reqId);
  },
  setIsLoading({ commit }, value) {
    commit('setIsLoading', value);
  },
  setCurrentHomeGroup({ commit, dispatch, rootState }, payload) {
    commit('setCurrentHomeGroup', payload);
    dispatch('setCurrentCameraGroupRole', payload);
    const avatarId = rootState.settings.currentAvatarId;
    CameraService.saveSelectedHomeGroup(payload, avatarId);
  },
  async setCurrentCameraGroupRole({ commit, rootState }, groupId) {
    if (
      rootState.settings.currentAvatarCameraRoles &&
      rootState.settings.currentAvatarCameraRoles.length > 0
    ) {
      const policies = rootState.settings.currentAvatarCameraRoles.find(
        (g) => +g.cameraGroupId === +groupId
      );

      const cameraPolicy = {
        access: true,
        liveOnly: true,
      };

      if (policies && policies.policyItems.length) {
        const accessPolicy = policies.policyItems.find(
          (item) => item.name === 'CameraPolicy_Access'
        );
        const liveOnlyPolicy = policies.policyItems.find(
          (item) => item.name === 'CameraPolicy_LiveOnly'
        );

        cameraPolicy.access = !!accessPolicy && accessPolicy.value === 1;
        cameraPolicy.liveOnly = !!liveOnlyPolicy && liveOnlyPolicy.value === 1;
      }
      commit('setCurrentCameraPolicy', cameraPolicy);
    }
  },
  async getWebVersion({ commit }) {
    commit('setWebVersion', process.env.VUE_APP_WEB_VERSION);
  },
  async setCameraListLoading({ commit }, status) {
    commit('setCameraListLoading', status);
  },
  async attachCamerasCredentials({ commit }, payload) {
    const { ids } = payload;

    const cameraList = ids.map((id) => ({ id }));
    await videoService.attachCameraIamAccessKey(cameraList);

    cameraList.forEach((cam) => {
      commit('updateCamera', cam);
    });
  },
  async noCameraFound({ commit }, payload) {
    commit('noCameraFound', payload);
  },
  async initializeUserAndCameras({ dispatch }) {
    await dispatch('settings/fetchInvitedUsers', null, { root: true });
    // NOTE: getCameraListWithGroup() must be called after fetchInvitedUsers()
    const list = await videoService.getCameraListWithGroup();
    await dispatch('noCameraFound', !list?.cameraLists?.length);
    return list;
  },
  setHomeCameraGroups({ commit }, payload) {
    commit('setHomeCameraGroups', {
      groups: cloneDeep(payload),
      list: cloneDeep(Object.values(payload)),
    });
  },
  updateCameraGroupOrder({ commit }, payload) {
    commit('updateCameraGroupOrder', payload);
  },
  async fetchReorderCameraGroup(
    { dispatch, state },
    { noSelectedGroupChange = false } = {}
  ) {
    const result = await getCameraGroupList();
    if (result?.tags) {
      await dispatch('changeCameraList', {
        cameraList: state.cameraList,
        groups: result,
        noSelectedGroupChange,
      });

      result.tags.forEach((newTag) => {
        const homeCameraGroups = state.homeCameraGroups;

        const tag = homeCameraGroups[newTag.id];
        if (tag) {
          const cameraList = tag.cameraList;
          const sortedCameraList = sortObjectsByIds(
            newTag.cameraIds,
            cloneDeep(cameraList),
            false
          );
          dispatch('updateCameraGroupOrder', {
            groupId: tag.id,
            cameraList: sortedCameraList,
          });
          dispatch(
            'settings/updateCameraGroupOrder',
            {
              groupId: tag.id,
              ids: sortedCameraList.map((c) => c.id),
            },
            { root: true }
          );
        }
      });
    }
    return { tags: result?.tags };
  },
  clearInitialPage({ commit }) {
    commit('clearInitialPage');
  },
  async processEventId({ commit, state }, { eventid }) {
    try {
      // fetch event data
      const event = await eventService.getEventByEventId(eventid);
      if (!event || !event.cameraId) {
        console.warn(`[Store] Event ${eventid} not found or missing cameraId`);
        return;
      }

      // get camera groups from store
      const homeCameraGroups = state.homeCameraGroups;
      const currentHomeGroup = state.currentHomeGroup;

      if (!homeCameraGroups || Object.keys(homeCameraGroups).length === 0) {
        console.warn('[Store] Camera groups not loaded yet');
        return;
      }

      // check if event camera is in current group (scope: only handle events in current group)
      const targetCameraId = event.cameraId;
      const currentGroup =
        currentHomeGroup && homeCameraGroups[currentHomeGroup]
          ? homeCameraGroups[currentHomeGroup]
          : null;

      if (!currentGroup || !currentGroup.cameraList) {
        console.warn('[Store] Current camera group not available');
        return;
      }

      // only proceed if event camera is in current group
      const cameraIndex = currentGroup.cameraList.findIndex(
        (cam) => String(cam.id) === String(targetCameraId)
      );

      if (cameraIndex === -1) {
        console.warn(
          `[Store] Event camera ${targetCameraId} not in current group. Skipping eventid processing.`
        );
        // do not process - event camera is in different group (out of scope for this fix)
        return;
      }

      // calculate target page
      const targetPage = cameraIndex + 1; // page is 1-indexed

      // store initial page in store (HomeVideo will read this)
      commit('setInitialPage', targetPage);

      // store event data for HomeVideo to use
      eventService.setEvent(event);

      // remove eventid from URL query parameters after processing
      const currentQuery = { ...router.currentRoute.query };
      delete currentQuery.eventid;
      router.replace({
        path: router.currentRoute.path,
        query: currentQuery,
      });
    } catch (error) {
      console.error('[Store] Error processing eventid:', error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
